import { useEffect, useRef, useState } from 'react';

/**
 * This hook calculates he offset required to display cell acctions correctly,
 * and also sets a CSS variable with the table width
 * this is used to calculate the width of the Header elements when the window is resized.
 * We porposly emit window resize event from other components to trigger the recalculation of the header elements width.
 */
export const useHeaderElementsOffsets = tableWrapperRef => {
  const scrollableHeaderElementRef = useRef<HTMLDivElement>(null);
  const fixedHeaderElementRef = useRef<HTMLDivElement>(null);
  const fixedContentHeightRef = useRef<number>(0);
  const scrollableContentHeightRef = useRef<number>(0);
  const scrollHeightRef = useRef<number>(0);

  const [actionsOffsetTop, setActionsOffsetTop] = useState<number>(0);

  const updateActionsOffsetTop = () => {
    if (scrollableContentHeightRef.current) {
      const delta = scrollableContentHeightRef.current - (scrollHeightRef.current ?? 0);
      const padding = delta > 0 ? delta : 0;

      setActionsOffsetTop(padding + fixedContentHeightRef.current);
    } else {
      setActionsOffsetTop(0);
    }
  };

  const updateTableHeaderHeight = () => {
    let fixedHeaderElementHeight = 0;
    let scrollableHeaderElementHeight = 0;

    if (fixedHeaderElementRef.current) {
      fixedHeaderElementHeight = fixedHeaderElementRef.current.getBoundingClientRect().height;
      fixedContentHeightRef.current = fixedHeaderElementHeight;
    }

    if (scrollableHeaderElementRef.current) {
      scrollableHeaderElementHeight = scrollableHeaderElementRef.current.getBoundingClientRect().height;
      scrollableContentHeightRef.current = scrollableHeaderElementHeight;
    }

    if (tableWrapperRef.current) {
      tableWrapperRef.current.style.setProperty(
        '--table-header-elements-height',
        `${fixedHeaderElementHeight + scrollableHeaderElementHeight}px`,
      );
    }
  };

  const updateSizeVariables = () => {
    if (tableWrapperRef.current) {
      const { width } = tableWrapperRef.current.getBoundingClientRect();
      const scrollbarWidth = tableWrapperRef.current.offsetWidth - tableWrapperRef.current.clientWidth;

      const adjustedWidth = width - scrollbarWidth;

      tableWrapperRef.current.style.setProperty('--table-wrapper-width', `${adjustedWidth}px`);
    }
  };

  const runUpdates = () => {
    if (tableWrapperRef.current) {
      requestAnimationFrame(() => {
        updateSizeVariables();
        updateTableHeaderHeight();
        updateActionsOffsetTop();
      });
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(runUpdates);

    if (tableWrapperRef.current) {
      resizeObserver.observe(tableWrapperRef.current);
    }

    if (scrollableHeaderElementRef.current) {
      resizeObserver.observe(scrollableHeaderElementRef.current);
    }

    if (fixedHeaderElementRef.current) {
      resizeObserver.observe(fixedHeaderElementRef.current);
    }

    runUpdates();
    window.addEventListener('resize', runUpdates);

    return () => {
      window.removeEventListener('resize', runUpdates);
      resizeObserver.disconnect();
    };
  }, [tableWrapperRef.current, scrollableHeaderElementRef.current, fixedHeaderElementRef.current]);

  const setScrollHeight = (height: number) => {
    scrollHeightRef.current = height;
    runUpdates();
  };

  return { fixedHeaderElementRef, scrollableHeaderElementRef, setScrollHeight, actionsOffsetTop };
};
